import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Avatar,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Breadcrumbs,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ManagerApi from '../../api/ManagerApi';
import { CreateProsecutorDto, FullCourtDto } from '../../interfaces/CourtInterfaces';
import './ManagerCreateProsecutorView.scss';

interface FormData {
  Prosecutor_Name: string;
  Prosecutor_Email: string;
  Prosecutor_Active: boolean;
}

interface FormErrors {
  Prosecutor_Name?: string;
  Prosecutor_Email?: string;
}

const initialFormData: FormData = {
  Prosecutor_Name: '',
  Prosecutor_Email: '',
  Prosecutor_Active: true
};

const ManagerCreateProsecutorView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const courtGuid = searchParams.get('Court_guid');
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [courtLoading, setCourtLoading] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [court, setCourt] = useState<FullCourtDto | null>(null);

  useEffect(() => {
    const fetchCourt = async () => {
      if (!courtGuid) {
        setSubmitError('Court ID is required');
        setCourtLoading(false);
        return;
      }

      try {
        setCourtLoading(true);
        const courtData = await ManagerApi.getCourt(courtGuid);
        setCourt(courtData);
      } catch (error) {
        setSubmitError(error instanceof Error ? error.message : 'Failed to fetch court details');
      } finally {
        setCourtLoading(false);
      }
    };

    fetchCourt();
  }, [courtGuid]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate Prosecutor Name
    if (!formData.Prosecutor_Name.trim()) {
      newErrors.Prosecutor_Name = 'Prosecutor name is required';
    } else if (formData.Prosecutor_Name.trim().length < 1 || formData.Prosecutor_Name.trim().length > 100) {
      newErrors.Prosecutor_Name = 'Prosecutor name must be between 1 and 100 characters';
    }

    // Validate Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.Prosecutor_Email.trim()) {
      newErrors.Prosecutor_Email = 'Prosecutor email is required';
    } else if (!emailRegex.test(formData.Prosecutor_Email.trim())) {
      newErrors.Prosecutor_Email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'Prosecutor_Active' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm() || !courtGuid) {
      return;
    }

    setLoading(true);
    setSubmitError(null);
    setSuccessMessage(null);

    try {
      const prosecutorData: CreateProsecutorDto = {
        Prosecutor_Name: formData.Prosecutor_Name.trim(),
        Prosecutor_Email: formData.Prosecutor_Email.trim(),
        Court_guid: courtGuid,
        Prosecutor_Active: formData.Prosecutor_Active
      };

      const createdProsecutor = await ManagerApi.createProsecutor(prosecutorData);
      setSuccessMessage(`Prosecutor "${createdProsecutor.Prosecutor_Name}" created successfully!`);
      
      // Reset form
      setFormData(initialFormData);
      setErrors({});

      // Navigate to the court page after a short delay
      setTimeout(() => {
        navigate(`/court?Court_guid=${courtGuid}`);
      }, 2000);

    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create prosecutor');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/court?Court_guid=${courtGuid}`);
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setErrors({});
    setSubmitError(null);
    setSuccessMessage(null);
  };

  if (courtLoading) {
    return (
      <div className="manager-create-prosecutor-view">
        <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        </Container>
      </div>
    );
  }

  if (submitError && !court) {
    return (
      <div className="manager-create-prosecutor-view">
        <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/courts')}
          >
            Back to Courts
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="manager-create-prosecutor-view">
      
      <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Court
          </Button>
          
          <Box flexGrow={1}>
            <Breadcrumbs sx={{ mb: 1 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/courts')}
                sx={{ textDecoration: 'none' }}
              >
                Courts
              </Link>
              <Link
                component="button"
                variant="body2"
                onClick={handleBack}
                sx={{ textDecoration: 'none' }}
              >
                {court?.Court_Name}
              </Link>
              <Typography color="text.primary">Add Prosecutor</Typography>
            </Breadcrumbs>
            
            <Typography variant="h4" component="h1" gutterBottom>
              Add New Prosecutor
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add a new prosecutor to {court?.Court_Name}
            </Typography>
          </Box>
        </Box>

        {court && (
          <Card elevation={2} sx={{ mb: 3, bgcolor: 'primary.50' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                  <AccountBalanceIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="primary.main">
                    {court.Court_Name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {court.Court_Address} • {court.Court_Email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current Prosecutors: {court.prosecutors.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
                <PersonIcon />
              </Avatar>
              <Typography variant="h6" component="h2">
                Prosecutor Information
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <form onSubmit={handleSubmit}>
              <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Prosecutor Name *
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  placeholder="Enter prosecutor's full name (e.g., John Smith)"
                  value={formData.Prosecutor_Name}
                  onChange={handleInputChange('Prosecutor_Name')}
                  error={!!errors.Prosecutor_Name}
                  helperText={errors.Prosecutor_Name || 'Maximum 100 characters'}
                  disabled={loading}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Prosecutor Email *
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="Enter prosecutor's email address"
                  value={formData.Prosecutor_Email}
                  onChange={handleInputChange('Prosecutor_Email')}
                  error={!!errors.Prosecutor_Email}
                  helperText={errors.Prosecutor_Email || 'This email must be unique across all prosecutors'}
                  disabled={loading}
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.Prosecutor_Active}
                      onChange={handleInputChange('Prosecutor_Active')}
                      disabled={loading}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        Prosecutor Status: {formData.Prosecutor_Active ? 'Active' : 'Inactive'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Active prosecutors can handle tickets and cases
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={loading}
                  size="large"
                >
                  Reset Form
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={loading}
                  size="large"
                >
                  {loading ? 'Creating Prosecutor...' : 'Add Prosecutor'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
};

export default ManagerCreateProsecutorView;
